// src/components/Login.js - LOGIN COMPONENT
import React, { useState } from 'react';
import { Building2, UserCheck, AlertCircle, Loader, Eye, EyeOff } from 'lucide-react';

const Login = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    memberNumber: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await onLogin(formData.memberNumber, formData.password);
      
      if (!result.success) {
        setError(result.error);
      }
      // If successful, App.js will handle navigation
    } catch (err) {
      setError('Unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (error) setError('');
  };

  const fillDemo = (memberNumber, password) => {
    setFormData({ memberNumber, password });
    setError('');
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <Building2 className="logo-icon" />
          <h1>EMCON Claims Portal</h1>
          <p>Submit your benefits claims online</p>
        </div>
        
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="memberNumber">Member Number</label>
            <input
              type="text"
              id="memberNumber"
              name="memberNumber"
              value={formData.memberNumber}
              onChange={handleChange}
              placeholder="e.g., S123, 2352, A029"
              required
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                required
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(v => !v)}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <p className="field-hint">First time signing in? Use your email address as your password.</p>
          </div>

          {error && (
            <div className="error-message">
              <AlertCircle className="error-icon" />
              <span>{error}</span>
            </div>
          )}
          
          <button
            type="submit"
            disabled={loading || !formData.memberNumber || !formData.password}
            className="login-button"
          >
            {loading ? (
              <>
                <Loader className="spinner" />
                <span>Signing In...</span>
              </>
            ) : (
              <>
                <UserCheck className="button-icon" />
                <span>Sign In</span>
              </>
            )}
          </button>
        </form>

        <div className="help-text">
          <p>Need help? Contact your benefits administrator.</p>
          <p>Can't find your member number? Check your benefits card or contact HR.</p>
        </div>
      </div>
    </div>
  );
};

export default Login;